<template>
  <div class="new-invoice concepts box wrapper-sale-note-concepts">
    <div class="title-container is-flex">
      <h3 class="title is-centered is-4 has-text-primary title-sale-note-concepts">
        Conceptos
      </h3>
      <div class="field is-centered">
        <BTooltip
          label="Agregar partida"
          type="is-success"
        >
          <a
            v-if="!disabled"
            class="button add-concept is-success is-rounded"
            @click="addRow"
          >
            <BIcon icon="plus" />
          </a>
        </BTooltip>
      </div>
    </div>
    <BTable
      :data="concepts"
      :columns="columns"
    >
      <template
        slot="header"
        slot-scope="props"
      >
        <span :class="{ 'header-label': props.column.label === 'SKU' }">
          {{ props.column.label }}
        </span>
        <AppTooltip
          v-if="props.column.meta"
          :label="props.column.meta"
        />
      </template>
      <template slot-scope="props">
        <BTableColumn
          field="description"
          internal
        >
          <AppInputField
            name="description"
            placeholder="Refrigerador"
            class="description-field"
            :disabled="isLoading || disabled"
            :class="{ 'selected-field': selected === `description-${props.row.id}` }"
            :value="props.row.description"
            required
            @input="onChange($event, props.row.id)"
            @change="onChange($event, props.row.id)"
            @focus="onChangeSelected(`description-${props.row.id}`)"
            @blur="onClearSelected"
          />
        </BTableColumn>
        <BTableColumn
          field="quantity"
          internal
        >
          <AppInputField
            name="quantity"
            placeholder="10"
            min="0.01"
            step="0.01"
            type="number"
            :disabled="isLoading || disabled"
            :value="props.row.quantity"
            required
            @input="onChange($event, props.row.id)"
            @change="onChange($event, props.row.id)"
          />
        </BTableColumn>
        <BTableColumn
          field="unit"
          internal
        >
          <AppInputField
            name="unit"
            placeholder="15.24"
            type="number"
            step="0.01"
            min="0.01"
            :disabled="isLoading || disabled"
            :value="props.row.unit"
            required
            @input="onChange($event, props.row.id)"
            @change="onChange($event, props.row.id)"
          />
        </BTableColumn>
        <BTableColumn
          field="amount"
          internal
        >
          {{ amountWithTaxes(props.row) | toCurrency(voucherCurrency) }}
        </BTableColumn>
        <BTableColumn
          field="actions"
          internal
        >
          <span class="actions">
            <a
              v-if="concepts.length > 1"
              class="button is-danger"
              @click="deleteRow(props.row.id)"
            >
              <BIcon
                icon="delete"
                aria-hidden="true"
              />
            </a>
          </span>
        </BTableColumn>
      </template>
      <template slot="footer">
        <th colspan="2" />
        <th>
          Totales
        </th>
        <th>
          {{ totalCurrency }}
        </th>
        <th />
      </template>
    </BTable>
  </div>
</template>

<script>
import merge from 'lodash/merge';
import debounce from 'lodash/debounce';

import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';

import { keyUnit } from '@machete/common-catalogs';

export default {
  name: 'SaleNoteConcepts',
  components: {
    AppTooltip: () => import(/* webpackChunkName: "app-tooltip" */ '@/components/AppTooltip.vue'),
    AppInputField: () => import(/* webpackChunkName: "app-input-field" */ '@/components/AppInputField'),
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: null,
      productServiceKeySearching: false,
      productServiceKeySearch: '',
      unitKeySearch: '',
      openedDetailed: [],
      columns: [
        {
          label: 'Descripción',
          field: 'description',
          centered: true,
        },
        {
          label: 'Cantidad',
          field: 'quantity',
          centered: true,
        },
        {
          label: 'Valor unitario',
          field: 'unit',
          centered: true,
        },
        {
          label: 'Importe',
          field: 'amount',
          centered: true,
        },
        {
          label: '',
          field: 'actions',
          centered: true,
        },
      ],
      unitKeyCatalog: keyUnit.map(
        ({ key, value }) => ({ value: key, label: `${key} - ${value}` }),
      ),
      conceptTemplate: () => ({
        productServiceKey: '',
        identificationNumber: '',
        quantity: null,
        unitKey: '',
        description: '',
        unit: null,
        amount: null,
      }),
    };
  },
  computed: merge(
    mapState({
      productServiceKeyCatalog: ({ ui }) => ui.catalogs.productServiceKey,
      voucherCurrency: ({ invoice }) => invoice.voucherCurrency,
      concepts: ({ invoice }) => invoice.concepts,
    }),
    mapGetters({
      totalCurrency: 'invoice/subTotalCurrency',
      taxesCurrency: 'invoice/taxesCurrency',
    }),
    {
      unitKeyObj() {
        // ku = keyUnit iteration
        return unitKey => this.unitKeyCatalog.find(ku => ku.value === unitKey);
      },
      amountWithTaxes() {
        return (concept) => {
          const unit = concept.unit || 0;
          const quantity = concept.quantity || 0;
          const result = unit * quantity;
          return result;
        };
      },
      productServiceKeyObj() {
        return (productServiceKey) => {
          const value = this.productServiceKeyCatalog.find(
            psk => psk.value === productServiceKey,
          );

          return value;
        };
      },
    },
  ),
  methods: merge(
    mapMutations({
      setValue: 'invoice/updateConcept',
      addConcept: 'invoice/addConcept',
      deleteConcept: 'invoice/deleteConcept',
    }),
    mapActions({ getCatalog: 'ui/getCatalog' }),
    {
      addRow() { this.addConcept(this.conceptTemplate()); },
      deleteRow(index) { this.deleteConcept(index); },
      taxesFromArray(taxes, unit, quantity) {
        return Array.isArray(taxes)
          ? taxes.reduce((accum, tax) => accum + ((unit * quantity) * parseFloat(tax.fee)), 0)
          : 0;
      },
      onChange(event, conceptId) {
        const { name, value } = event.target;
        const index = this.concepts.findIndex(({ id }) => id === conceptId);

        const accessor = `concepts.[${index}].${name}`;

        this.setValue({ accessor, value });
      },
      onChangeProductServiceKey({ value }, index) {
        this.onChange({ target: { name: 'productServiceKey', value } }, index);
      },
      onChangeUnitKey({ value }, index) {
        this.onChange({ target: { name: 'unitKey', value } }, index);
      },
      getProductServicesKeys: debounce(async function getData() {
        this.productServiceKeySearching = true;
        await this.getCatalog({
          catalogName: 'productServiceKey',
          search: this.productServiceKeySearch,
        });
        this.productServiceKeySearching = false;
      }),
      onSearch(search, loading) {
        loading(true);
        this.search(loading, search, this);
      },
      search: debounce(async (loading, search, vm) => {
        await vm.getCatalog({ catalogName: 'productServiceKey', search });
        loading(false);
      }, 650),
      onDetailsClose(row) {
        const index = this.openedDetailed.indexOf(row.id);
        this.openedDetailed.slice(index, 1);
      },
      onChangeSelected(id) {
        this.selected = id;
      },
      onClearSelected() {
        this.selected = '';
      },
      toggleDetail(id) {
        if (this.openedDetailed.includes(id)) {
          const index = this.openedDetailed.indexOf(id);
          this.openedDetailed.pop(index);
        } else {
          this.openedDetailed.push(id);
        }
      },
    },
  ),
};
</script>

<style scoped>
.title.is-4 {
  margin-bottom: 0;
}

.header-label {
  padding-right: 0.25rem;
}

.button.add-concept {
  padding-left: 0.75em!important;
  padding-right: 0.75em!important;
}

.button.add-concept .icon {
  margin-left: calc(-0.375em - 1px)!important;
  margin-right: calc(-0.375em - 1px)!important;
  font-size: inherit!important;
}

.add-concept-label {
  padding-left: .5rem;
}

.title-container {
  width: 100%;
  align-items: center;
  padding-bottom: .3rem;
  justify-content: space-between;
}

.product-service-key-field,
.product-service-key-field .field {
  width: 150px;
  max-width: 150px;
}

.selected-field.product-service-key-field,
.selected-field.product-service-key-field .field {
  width: 400px;
  max-width: 400px;
}

.unit-key-field,
.unit-key-field .field {
  width: 120px;
  max-width: 120px;
}

.selected-field.unit-key-field,
.selected-field.unit-key-field .field {
  width: 250px;
  max-width: 250px;
}

.description-field.selected-field {
  width: 400px;
}
</style>

<style>
.box.concepts th,
.box.concepts td {
  vertical-align: middle;
  text-align: center;
}

.box.concepts .label:not(:last-child) {
  margin-bottom: 0;
}

.new-invoice.concepts input[type='number'] {
  -moz-appearance: textfield;
}

.new-invoice.concepts input::-webkit-outer-spin-button,
.new-invoice.concepts input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.box.concepts .label:not(:last-child) {
  margin-right: 1rem;
}
</style>
